<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import MapIcon from "@/components/UI/icons/MapIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Pied de l'onglet Observations : sur quel ouvrage on travaille, et un unique
 * bouton d'actions qui ouvre les deux opérations possibles (changer d'ouvrage,
 * générer les constatations), chacune expliquée en une ligne.
 */

withDefaults(
  defineProps<{
    /** Nom de l'ouvrage lié ; vide = ouvrage sans nom */
    name?: string | null;
    /** La génération n'a de sens qu'avec au moins une observation */
    canGenerate?: boolean;
    disabled?: boolean;
  }>(),
  { canGenerate: false, disabled: false },
);

const emit = defineEmits<{
  (e: "change-link"): void;
  (e: "generate"): void;
}>();

const { t } = useTranslation();
</script>

<template>
  <div class="linked-diag-bar d-flex align-items-center gap-2">
    <MapIcon :size="20" class="flex-shrink-0" />
    <span class="linked-diag-name text-truncate flex-grow-1">
      {{ name || t("report:diagnostics.item.noname") }}
    </span>

    <el-dropdown
      trigger="click"
      placement="top-end"
      popper-class="canvas-popper"
      :disabled="disabled"
    >
      <DRBtnIcon
        variant="flat-white"
        :size="26"
        phosphor-icon="dots-three-outline"
        :disabled="disabled"
        :title="t('canvas:observations.footerActions.title')"
      />
      <template #dropdown>
        <div class="actions-popup">
          <button type="button" class="action-row" @click="emit('change-link')">
            <span class="action-icon">
              <PhosphorIcon name="arrows-clockwise" :size="18" />
            </span>
            <span class="action-text">
              <span class="action-title">
                {{ t("canvas:observations.footerActions.change.title") }}
              </span>
              <span class="action-desc">
                {{ t("canvas:observations.footerActions.change.description") }}
              </span>
            </span>
          </button>

          <button
            v-if="canGenerate"
            type="button"
            class="action-row"
            @click="emit('generate')"
          >
            <span class="action-icon ia">
              <PhosphorIcon name="sparkle" :size="18" weight="fill" />
            </span>
            <span class="action-text">
              <span class="action-title">
                {{ t("canvas:observations.footerActions.generate.title") }}
              </span>
              <span class="action-desc">
                {{
                  t("canvas:observations.footerActions.generate.description")
                }}
              </span>
            </span>
          </button>
        </div>
      </template>
    </el-dropdown>
  </div>
</template>

<style lang="scss" scoped>
.linked-diag-bar {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 6px;
  min-width: 0;
}

.linked-diag-name {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 500;
  min-width: 0;
}
</style>

<style lang="scss">
@import "@/style/variables.scss";

/* Non scopé : le contenu du dropdown est téléporté hors du composant */
.actions-popup {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 300px;
  padding: 4px;

  .action-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 10px;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
    }
  }

  .action-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    flex-shrink: 0;

    &.ia {
      background-color: rgba($ia, 0.18);
      color: lighten($ia, 12%);
    }
  }

  .action-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .action-title {
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.3;
  }

  .action-desc {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.35;
    white-space: normal;
  }
}
</style>
